import { useEffect, useMemo, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Campaign, Task, User } from '../../types';
import { SearchableDropdown } from '../../components/SearchableDropdown';
import { getTaskTypeDescriptor } from '../../taskTypes/descriptor';
import { listAvailableTasksForScope } from '../../services/tasks';
import { logGlobalError } from '../../utils/globalErrorLogger';

export interface AssignTaskRequest {
  taskTypeId: string;
  domainId: string | null;
  domainLabel: string | null;
  /** Omit/null to create a new blank task; set to assign an existing one. */
  taskId: string | null;
}

interface AssignTaskModalProps {
  campaign: Campaign;
  user: User;
  busy: boolean;
  onClose: () => void;
  onAssign: (req: AssignTaskRequest) => Promise<void>;
}

interface ScopeOption {
  key: string;
  taskTypeId: string;
  domainId: string | null;
  domainLabel: string | null;
  label: string;
}

/** One picker entry per domain (or per task type when it has no domains). */
function buildScopeOptions(campaign: Campaign): ScopeOption[] {
  const options: ScopeOption[] = [];
  for (const tt of campaign.taskTypes) {
    const mod = getTaskTypeDescriptor(tt.taskTypeId);
    const domains = tt.domains ?? [];
    if (domains.length > 0) {
      for (const d of domains) {
        options.push({
          key: `${tt.taskTypeId}::${d.domainId}`,
          taskTypeId: tt.taskTypeId,
          domainId: d.domainId,
          domainLabel: d.label,
          label: `${mod.shortName} · ${d.label}`,
        });
      }
    } else {
      options.push({
        key: tt.taskTypeId,
        taskTypeId: tt.taskTypeId,
        domainId: null,
        domainLabel: null,
        label: mod.shortName,
      });
    }
  }
  return options;
}

export default function AssignTaskModal({ campaign, user, busy, onClose, onAssign }: AssignTaskModalProps) {
  const scopeOptions = useMemo(() => buildScopeOptions(campaign), [campaign]);
  const [scopeKey, setScopeKey] = useState<string>(scopeOptions[0]?.key ?? '');
  const [source, setSource] = useState<'existing' | 'create'>('create');
  const [available, setAvailable] = useState<Task[] | null>(null);
  const [availableLoading, setAvailableLoading] = useState<boolean>(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const scope = useMemo(
    () => scopeOptions.find((o) => o.key === scopeKey) ?? null,
    [scopeOptions, scopeKey],
  );

  // Load the available-task pool whenever the selected scope changes.
  useEffect(() => {
    if (!scope) {
      setAvailable([]);
      return;
    }
    let cancelled = false;
    setAvailableLoading(true);
    setAvailable(null);
    setSelectedTaskId('');
    listAvailableTasksForScope(campaign.id, scope.taskTypeId, scope.domainId)
      .then((tasks) => {
        if (cancelled) return;
        setAvailable(tasks);
        // Default to picking an existing task when the pool has any; otherwise
        // fall back to creating a fresh one.
        setSource(tasks.length > 0 ? 'existing' : 'create');
        setSelectedTaskId(tasks[0]?.id ?? '');
      })
      .catch((e) => {
        if (cancelled) return;
        setAvailable([]);
        setSource('create');
        setError(e?.message || 'Failed to load available tasks');
        logGlobalError(e, {
          context: 'AssignTaskModal.loadAvailable',
          metadata: { campaignId: campaign.id, taskTypeId: scope.taskTypeId, domainId: scope.domainId },
        });
      })
      .finally(() => {
        if (!cancelled) setAvailableLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [campaign.id, scope]);

  const canSubmit =
    !!scope && !busy && (source === 'create' || (source === 'existing' && !!selectedTaskId));

  const submit = async () => {
    if (!scope || !canSubmit) return;
    setError(null);
    try {
      await onAssign({
        taskTypeId: scope.taskTypeId,
        domainId: scope.domainId,
        domainLabel: scope.domainLabel,
        taskId: source === 'existing' ? selectedTaskId : null,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to assign task');
    }
  };

  const availableCount = available?.length ?? 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="assign-task-title"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div onClick={(e) => e.stopPropagation()} className="mt-16 w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div className="min-w-0">
            <h2 id="assign-task-title" className="text-lg font-semibold text-gray-900">
              Assign task
            </h2>
            <p className="mt-0.5 truncate text-xs text-gray-500">
              {user.displayName || user.email || user.uid}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            disabled={busy}
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div>
            <SearchableDropdown
              id="assign-scope"
              label="Domain"
              options={scopeOptions.map((option) => ({ value: option.key, label: option.label }))}
              value={scopeKey || null}
              disabled={busy || scopeOptions.length === 0}
              onChange={(value) => {
                if (value !== null) setScopeKey(value);
              }}
              placeholder={scopeOptions.length > 0 ? 'Select a domain' : 'No domains available'}
              searchPlaceholder="Search domains"
              preserveOrder
              triggerClassName="mt-1"
            />
          </div>

          <fieldset className="space-y-2">
            <legend className="text-xs font-medium text-gray-700">Task</legend>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="assign-source"
                value="existing"
                checked={source === 'existing'}
                disabled={busy || availableLoading || availableCount === 0}
                onChange={() => setSource('existing')}
                className="mt-1"
              />
              <span className="min-w-0">
                <span className="font-medium text-gray-900">Pick an available task</span>
                <span className="ml-1 text-xs text-gray-500">
                  {availableLoading ? '(loading…)' : `(${availableCount} available)`}
                </span>
                {source === 'existing' && availableCount > 0 && (
                  <SearchableDropdown
                    id="assign-available-task"
                    options={(available ?? []).map((task) => ({
                      value: task.id,
                      label: task.title || task.id,
                      searchText: `${task.title ?? ''} ${task.id}`,
                    }))}
                    value={selectedTaskId || null}
                    disabled={busy}
                    onChange={(value) => setSelectedTaskId(value ?? '')}
                    placeholder="Select an available task"
                    searchPlaceholder="Search available tasks"
                    ariaLabel="Available task"
                    preserveOrder
                    className="mt-1.5"
                  />
                )}
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="assign-source"
                value="create"
                checked={source === 'create'}
                disabled={busy}
                onChange={() => setSource('create')}
                className="mt-1"
              />
              <span>
                <span className="font-medium text-gray-900">Create a new task</span>
                <span className="ml-1 text-xs text-gray-500">in this domain</span>
              </span>
            </label>
          </fieldset>

          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {source === 'create' ? 'Create & assign' : 'Assign task'}
          </button>
        </div>
      </div>
    </div>
  );
}
